import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { streamAgentQuery } from '../../services/kbQueryService';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ArrowUp, Square, SparklesIcon, ChevronRight} from 'lucide-react';

const AIPanel = styled.div`
  background: white;
  border-radius: 12px;
  height: calc(100dvh - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: sticky;
  top: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, rgb(0, 180, 216) 0%, rgb(168, 85, 247) 100%);
    padding: 2px;
`;

const AIHeader = styled.div`
  background: linear-gradient(135deg, #00B4D8 0%, #A855F7 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
`;

const AIIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const AIHeaderContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AITitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: white;
`;

const DropdownIcon = styled.div`
  color: white;
  font-size: 1rem;
  cursor: pointer;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.2rem;
  background: #F9FAFB;
  border-radius: 12px 12px 0px 0px;
  font-weight: 600;
  overscroll-behavior: contain;
  position: relative;
`;

const GreetingText = styled.div`
  font-size: 1.1rem;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const ContextText = styled.div`
  font-size: 0.85rem;
  color: #6B7280;
  margin-bottom: 1.5rem;
`;

const ExecutiveSummaryCard = styled.div`
  background: white;
  border: 1px solid #00b928;
  border-radius: 12px;
  padding: 1.7rem;
  margin-bottom: 1.5rem;
`;

const SummaryTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const SummarySection = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;
const ChatDivider = styled.div`
  height: 1px;
  background: #E5E7EB;
  margin: 1rem 0;
  
`;
const SectionLabel = styled.div`
  font-size: 0.75rem;
  color: #9CA3AF;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionValue = styled.div`
  font-size: 0.9rem;
  color: #111827;
  font-weight: 500;
  
  &.success {
    color: #10B981;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  
  &.amount {
    font-weight: 600;
  }
`;

const KeyFindingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const KeyFindingItem = styled.div`
  font-size: 0.85rem;
  color: #374151;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  
  &::before {
    content: '✓';
    color: #10B981;
    font-weight: 600;
    flex-shrink: 0;
  }
`;

const ViewSummaryButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #ECFDF5;
  color: #10B981;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.8rem;
  transition: background 0.2s;
  
  &:hover {
    background: #D1FAE5;
  }
`;

const QuestionPrompt = styled.div`
  font-size: 0.85rem;
  color: #6B7280;
  margin-bottom: 1rem;
`;

const ChatMessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageBubble = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 85%;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const MessageHeader = styled.div`
  font-size: 0.75rem;
  color: #6B7280;
  margin-bottom: 0.25rem;
  padding: 0 0.5rem;
`;

const MessageText = styled.div`
  background: ${props => props.isUser ? '#0EA5E9' : '#F3F4F6'};
  color: ${props => props.isUser ? 'white' : '#111827'};
  padding: 0.75rem 1rem;
  border-radius: ${props => props.isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  font-size: 0.875rem;
  line-height: 1.5;
  word-wrap: break-word;
  
  p {
    margin: 0 0 0.5rem 0;
    &:last-child { margin-bottom: 0; }
  }
  
  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin: 0.25rem 0;
  }
  
  code {
    background: ${props => props.isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)'};
    padding: 0.125rem 0.25rem;
    border-radius: 3px;
    font-size: 0.85em;
  }
`;

const AssistantMarkdown = styled.div`
  font-size: 0.9rem;
  line-height: 1.6;
  color: #374151;

  max-width: 640px;
  width: 100%;

  h1, h2, h3, h4 {
    font-weight: 600;
    color: #111827;
    margin-top: 1.1rem;
    margin-bottom: 0.4rem;
  }

  h1 {
    font-size: 1.05rem;
  }

  h2 {
    font-size: 1rem;
  }

  h3 {
    font-size: 0.95rem;
  }

  h4 {
    font-size: 0.9rem;
  }

  p {
    margin: 0 0 0.5rem 0;
  }

  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.25rem;
  }

  li {
    margin: 0.25rem 0;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  hr {
    border: none;
    border-top: 1px solid #E5E7EB;
    margin: 1rem 0;
  }

  blockquote {
    border-left: 3px solid #E5E7EB;
    margin: 0.75rem 0;
    padding-left: 0.75rem;
    color: #4B5563;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0 0.5rem;
    font-size: 0.85rem;
  }

  thead th {
    background: #F3F4F6;
    font-weight: 600;
  }

  th,
  td {
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid #E5E7EB;
    text-align: left;
    vertical-align: top;
  }

  tbody tr:nth-child(even) {
    background: #F9FAFB;
  }

  code {
    background: rgba(15, 23, 42, 0.04);
    padding: 0.15rem 0.3rem;
    border-radius: 4px;
    font-size: 0.85em;
  }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9CA3AF;
    animation: bounce 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
  
  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SuggestionButton = styled.div`
  font-size: 0.85rem;
  color: #0EA5E9;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0.5rem  1rem ;
  background-color: rgba(14, 165, 233, 0.05);
  border-radius: 25px;
  
  &:hover {
    color: #0284C7;
    text-decoration: underline;
  }
`;

const BulletPoint = styled.span`
  display: none;
`;

const InputContainer = styled.div`
  
  padding: 1rem 1.2rem;
  background: #F9FAFB;
  flex-shrink: 0;
  position: relative;
  border-radius: 0px 0px 12px 12px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PlusIcon = styled.div`
  position: absolute;
    left: 0.7rem;
    color: rgb(14, 165, 233);
    font-size: 1.7rem;
    font-weight: 400;
    pointer-events: none;
    width: 30px;
    height: 30px;
    background-color: rgba(14, 164, 233, 0.05);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 3.5rem 0.875rem 2.95rem;
  border: 2px solid #0EA5E9;
  border-radius: 30px;
  font-size: 0.9rem;
  outline: none;
  background: white;
  color: #111827;
  box-shadow: 0px 20px 30px rgba(0, 0, 0, 0.05);
  
  &:focus {
    border-color: #0284C7;
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:disabled {
    background: #F3F4F6;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: 0.5rem;
  background: ${props => props.disabled ? '#D1D5DB' : '#0EA5E9'};
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #0284C7;
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const ProgressContainer = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 102, 255, 0.1);
  flex-shrink: 0;
`;

const ProgressText = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const ProgressValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0066FF;
`;

const MetaClaimsAI = ({ 
  userName = "Satishkumar",
  userEmail = "",
  userId = null,
  sessionId = null,
  plannerCd = null,
  chatContext = "listing",
  otherDetails = {},
  contextMessage = "Ask me about this claim or any part of the claim.",
  initialMessage,
  initialHiddenQuery = null,
  suggestions = [],
  showProgress = false,
  progressValue = "55%",
  progressLabel = "Progress",
  onClose
}) => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isChatMode, setIsChatMode] = useState(false);
  const [localSessionId] = useState(() => uuidv4());
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const initialHiddenSentRef = useRef(false);
  const initialMessageAppliedRef = useRef(false);
  const basePath = process.env.PUBLIC_URL || "";


  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!initialHiddenQuery) return;
    if (initialHiddenSentRef.current) return;
    initialHiddenSentRef.current = true;
    handleSend(initialHiddenQuery, { silent: true });
  }, [initialHiddenQuery]);

  useEffect(() => {
    if (!initialMessage) return;
    if (initialMessageAppliedRef.current) return;
    initialMessageAppliedRef.current = true;
    setIsChatMode(true);
    setMessages(prev => [
      ...prev,
      {
        id: `initial-assistant-${Date.now()}`,
        role: 'assistant',
        content: initialMessage,
        isStreaming: false,
      },
    ]);
  }, [initialMessage]);

  const handleStreamingMessage = (obj) => {
    if (!obj) return;

    if (obj.type === 'final_response' || obj.type === 'partial_response') {
      const content = obj.content || '';
      setMessages(prev => {
        const msgs = [...prev];
        // Find the last assistant message and update it
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i].role === 'assistant') {
            msgs[i] = { ...msgs[i], content: msgs[i].content + content };
            return msgs;
          }
        }
        return msgs;
      });
    }

    if (obj.type === 'stream_done') {
      setIsStreaming(false);
      setMessages(prev => prev.map(msg => 
        msg.role === 'assistant' && msg.isStreaming
          ? { ...msg, isStreaming: false }
          : msg
      ));
    }
  };

  const handleSend = async (message = inputValue, options = {}) => {
    const { silent = false } = options;
    const query = message.trim();
    if (!query || isStreaming) return;

    setIsChatMode(true);
    setInputValue('');
    
    if (!silent) {
      // Add user message
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'user',
        content: query
      }]);
    }

    // Add empty assistant message placeholder
    setMessages(prev => [...prev, {
      id: `agent-${Date.now()}`,
      role: 'assistant',
      content: '',
      isStreaming: true
    }]);

    setIsStreaming(true);

    try {
      const effectiveUserId = userId || userEmail;
      const effectiveSessionId = sessionId || localSessionId;

      if (chatContext === 'manual_in_detail') {
        const pipelineCd = otherDetails?.pipelineCd || otherDetails?.pipeline_cd || null;
        const pipelineInstanceId = otherDetails?.pipelineInstanceId || otherDetails?.pipeline_instance_id || null;
        const nodeCd = otherDetails?.nodeCd || otherDetails?.node_cd || null;

        if (!pipelineCd || !pipelineInstanceId || !nodeCd) {
          console.warn('Missing identifiers for manual_in_detail Copilot query', {
            pipelineCd,
            pipelineInstanceId,
            nodeCd,
          });
          setMessages(prev => prev.map(msg =>
            msg.role === 'assistant' && msg.isStreaming
              ? {
                  ...msg,
                  content: 'Copilot is not available for this case yet because some identifiers are missing.',
                  isStreaming: false,
                }
              : msg
          ));
          setIsStreaming(false);
          return;
        }

        const inDetailRequestBody = {
          query,
          user_id: effectiveUserId,
          session_id: effectiveSessionId,
          pipeline_cd: pipelineCd,
          pipeline_instance_id: pipelineInstanceId,
          node_cd: nodeCd,
        };

        await streamAgentQuery(
          inDetailRequestBody,
          '/api/v1/mms_ai_executors/manual_in_detail_task_query/execute/sync/stream',
          handleStreamingMessage
        );
      } else {
        const requestBody = {
          query: query,
          session_id: effectiveSessionId,
          user_id: effectiveUserId,
          payload: {}
        };

        if (plannerCd) {
          requestBody.planner_cd = plannerCd;
        }

        await streamAgentQuery(
          requestBody,
          '/api/v1/mms_ai_executors/manual_task_query/execute/sync/stream',
          handleStreamingMessage
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.map(msg => 
        msg.role === 'assistant' && msg.isStreaming
          ? { ...msg, content: 'Sorry, I encountered an error processing your request. Please try again.', isStreaming: false }
          : msg
      ));
      setIsStreaming(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <AIPanel>
      <AIHeader>
        <AIHeaderContent>
          <AIIcon><img src={`${basePath}/images/copilot-icon.png`} alt="MetaClaims AI" style={{ width: '40px', height: '40px' }} /></AIIcon>
          {/* <SparklesIcon color='#ffffff' fill='#ffffff' size={24} /> */}
          <AITitle>MetaClaims AI</AITitle>
        </AIHeaderContent>
        <DropdownIcon onClick={onClose}><ChevronRight size={20} /></DropdownIcon>

      </AIHeader>

      <MessageContainer ref={messageContainerRef}>
        {!isChatMode ? (
          <>
            <GreetingText>👋 Hi {userName},</GreetingText>
            <ContextText>Here's a quick summary of this claim</ContextText>
            <ChatDivider></ChatDivider>
            <ExecutiveSummaryCard>
          <SummaryTitle>Executive Summary</SummaryTitle>
          
          <SummarySection>
            <SectionLabel>Status</SectionLabel>
            <SectionValue className="success">
              <span>✓</span> Approved with Adjustment
            </SectionValue>
          </SummarySection>
          <ChatDivider></ChatDivider>
          <SummarySection>
            <SectionLabel>Financial Impact</SectionLabel>
            <SectionValue className="amount">IDR23,300 reduction (17%)</SectionValue>
          </SummarySection>
          <ChatDivider></ChatDivider>
          <SummarySection>
            <SectionLabel>Key Findings</SectionLabel>
            <KeyFindingsList>
              <KeyFindingItem>Policy rule R-9.0 applied</KeyFindingItem>
              <KeyFindingItem>Billing deviation corrected</KeyFindingItem>
              <KeyFindingItem>Clinical validation confirmed</KeyFindingItem>
            </KeyFindingsList>
          </SummarySection>
          <ChatDivider></ChatDivider>
          <SummarySection>
            <SectionLabel>Recommendation</SectionLabel>
            <SectionValue>Submit claim at adjusted payable amount IDR 104,500</SectionValue>
          </SummarySection>
          <ViewSummaryButton>
            <span><img src={`/images/ai-star-icon-green.png`} alt="AI Star" style={{ width: '20px', height: '20px' }} /></span> View Full AI Summary
          </ViewSummaryButton>
            </ExecutiveSummaryCard>

            <QuestionPrompt>Ask me if you'd like details on any part of the claim.</QuestionPrompt>

            {suggestions.length > 0 && (
              <SuggestionsContainer>
                {suggestions.map((suggestion, index) => (
                  <SuggestionButton 
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </SuggestionButton>
                ))}
              </SuggestionsContainer>
            )}
          </>
        ) : (
          <ChatMessagesContainer>
            {messages.map((message) => (
              <MessageBubble key={message.id} isUser={message.role === 'user'}>
                <MessageHeader>
                  {message.role === 'user' ? 'You' : 'MetaClaims AI'}
                </MessageHeader>
                <MessageText isUser={message.role === 'user'}>
                  {message.role === 'user' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  ) : (
                    message.content ? (
                      <AssistantMarkdown>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                      </AssistantMarkdown>
                    ) : (
                      <LoadingDots>
                        <span></span>
                        <span></span>
                        <span></span>
                      </LoadingDots>
                    )
                  )}
                </MessageText>
              </MessageBubble>
            ))}
            <div ref={messagesEndRef} />
          </ChatMessagesContainer>
        )}
      </MessageContainer>

      <InputContainer>
        <InputWrapper>
          <PlusIcon>+</PlusIcon>
          <Input
            type="text"
            placeholder="How can I help you today?"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isStreaming}
          />
          <SendButton 
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isStreaming}
          >
            {isStreaming ? <Square size={10} fill="#ffffff" /> : <ArrowUp size={20} />}
          </SendButton>
        </InputWrapper>
      </InputContainer>
    </AIPanel>
  );
};

export default MetaClaimsAI;
